
import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, getCountFromServer, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { createConnection as apiCreateConnection, updateConnection as apiUpdateConnection, reconnectConnection as apiReconnectConnection, deleteConnection as apiDeleteConnection } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import analytics from '@/lib/analytics';
import { AnalyticsEvents, AnalyticsParams } from '@/lib/analytics/events';

// ADC-IMPLEMENTS: <adc-006a-feature-conncard-01>
export interface Connection {
  id: string;
  name: string;
  status: string;
  createdAt: Date;
  updatedAt: Date;
  ageRating?: string;
  qr?: string;
  error?: string;
  flaggedMessageCount?: number;
  totalFlaggedMessageCount?: number; // Add total count regardless of review time
  // ADC-006A v2.0.0: activeIncidentCount removed -- incidents are browsed from IncidentDashboard
}

export const useConnections = () => {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const getDocumentCount = async (
    collectionPath: string, 
    whereConditions?: Array<[string, any, any]>,
    includeTimestampFilter: boolean = true
  ) => {
    try {
      console.log(`Getting count for collection: ${collectionPath}`);
      console.log(`With where conditions:`, whereConditions);
      console.log(`Including timestamp filter: ${includeTimestampFilter}`);
      
      const collectionRef = collection(db, collectionPath);
      let countQuery = query(collectionRef);
      
      if (whereConditions && whereConditions.length > 0) {
        // Apply conditions based on the includeTimestampFilter flag
        const filteredConditions = includeTimestampFilter 
          ? whereConditions 
          : whereConditions.filter(condition => condition[0] !== "messageTimestamp");
        
        if (filteredConditions.length > 0) {
          // Apply the first condition
          countQuery = query(collectionRef, where(filteredConditions[0][0], filteredConditions[0][1], filteredConditions[0][2]));
          
          // Apply any additional conditions
          for (let i = 1; i < filteredConditions.length; i++) {
            countQuery = query(countQuery, where(filteredConditions[i][0], filteredConditions[i][1], filteredConditions[i][2]));
          }
        }
      }
      
      const countSnapshot = await getCountFromServer(countQuery);
      const count = countSnapshot.data().count;
      console.log(`Count result for ${collectionPath}: ${count}`);
      return count;
    } catch (err) {
      console.error(`Error getting count for ${collectionPath}:`, err);
      
      try {
        console.log(`Trying fallback approach for ${collectionPath}`);
        const collectionRef = collection(db, collectionPath);
        let docsQuery = query(collectionRef);
        
        if (whereConditions && whereConditions.length > 0) {
          // Apply conditions based on the includeTimestampFilter flag
          const filteredConditions = includeTimestampFilter 
            ? whereConditions 
            : whereConditions.filter(condition => condition[0] !== "messageTimestamp");
          
          if (filteredConditions.length > 0) {
            // Apply the first condition
            docsQuery = query(collectionRef, where(filteredConditions[0][0], filteredConditions[0][1], filteredConditions[0][2]));
            
            // Apply any additional conditions
            for (let i = 1; i < filteredConditions.length; i++) {
              docsQuery = query(docsQuery, where(filteredConditions[i][0], filteredConditions[i][1], filteredConditions[i][2]));
            }
          }
        }
        
        const docsSnapshot = await getDocs(docsQuery);
        console.log(`Fallback count for ${collectionPath}: ${docsSnapshot.size}`);
        return docsSnapshot.size;
      } catch (fallbackErr) {
        console.error(`Fallback approach also failed for ${collectionPath}:`, fallbackErr);
        return 0;
      }
    }
  };

  useEffect(() => {
    if (!user) {
      setConnections([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const connectionsRef = collection(db, 'connections');
    const userConnectionsQuery = query(connectionsRef, where('userId', '==', user.uid));

    const unsubscribe = onSnapshot(
      userConnectionsQuery,
      async (snapshot) => {
        try {
          const connectionPromises = snapshot.docs.map(async (docSnapshot) => {
            const data = docSnapshot.data();
            const connectionId = docSnapshot.id;
            
            // Only focus on conversations collection, ignoring the outdated messages collection
            const conversationsPath = `connections/${connectionId}/conversations`;
            
            let flaggedCount = 0;
            let totalFlaggedCount = 0;
            // ADC-006A v2.0.0: incident count queries removed (now in IncidentDashboard)
            
            try {
              // First get all conversations in this connection to get their actual IDs
              const conversationsRef = collection(db, conversationsPath);
              const conversationsSnapshot = await getDocs(conversationsRef);
              
              console.log(`Found ${conversationsSnapshot.size} conversations for connection ${connectionId}`);
              
              if (!conversationsSnapshot.empty) {
                // Now for each actual conversation, count the flagged messages
                const flaggedPromises = conversationsSnapshot.docs.map(async (convDoc) => {
                  const convId = convDoc.id;
                  const convData = convDoc.data();
                  const messagesPath = `connections/${connectionId}/conversations/${convId}/messages`;
                  
                  // Get lastFlaggedReviewAt timestamp if it exists
                  const lastFlaggedReviewAt = convData.lastFlaggedReviewAt ? convData.lastFlaggedReviewAt.toDate() : null;
                  
                  // Set up conditions for the query
                  const queryConditions: Array<[string, any, any]> = [
                    ["isFlaggedMessage", "==", true]
                  ];
                  
                  // Add timestamp condition if lastFlaggedReviewAt exists
                  if (lastFlaggedReviewAt) {
                    queryConditions.push(["messageTimestamp", ">", Math.floor(lastFlaggedReviewAt.getTime() / 1000)]);
                  }
                  
                  // Count flagged messages since last review in this conversation
                  const unreviewedCount = await getDocumentCount(messagesPath, queryConditions, true);
                  
                  // Count all flagged messages in this conversation
                  const allFlaggedCount = await getDocumentCount(messagesPath, queryConditions, false);
                  
                  return { unreviewedCount, allFlaggedCount };
                });
                
                // Sum up all the flagged message counts
                const flaggedResults = await Promise.all(flaggedPromises);
                flaggedCount = flaggedResults.reduce((total, result) => total + result.unreviewedCount, 0);
                totalFlaggedCount = flaggedResults.reduce((total, result) => total + result.allFlaggedCount, 0);

                console.log(`Connection ${connectionId} has ${flaggedCount} unreviewed flagged messages and ${totalFlaggedCount} total flagged messages across ${conversationsSnapshot.size} conversations`);
              }
            } catch (err) {
              console.error(`Error counting flagged messages for connection ${connectionId}:`, err);
            }

            return {
              id: connectionId,
              name: data.name || 'Unnamed Connection',
              status: data.status || 'unknown',
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date(),
              ageRating: data.ageRating || "12+",
              qr: data.qr,
              error: data.error,
              flaggedMessageCount: flaggedCount,
              totalFlaggedMessageCount: totalFlaggedCount,
              // ADC-006A v2.0.0: activeIncidentCount removed
            };
          });
          
          const connectionList = await Promise.all(connectionPromises);
          
          connectionList.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
          
          setConnections(connectionList);
          setLoading(false);
        } catch (err) {
          console.error('Error processing connections:', err);
          setError('Failed to load your connections. Please try again later.');
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error fetching connections:', err);
        setError('Failed to load your connections. Please try again later.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  const handleCreateConnection = async (name: string, ageRating: string): Promise<string | undefined> => {
    try {
      if (!user) {
        toast({
          title: 'Authentication required',
          description: 'You must be logged in to create a connection.',
          variant: 'destructive',
        });
        return undefined;
      }
      
      const result = await apiCreateConnection(name, ageRating);
      
      if (!result.success || !result.data?.id) {
        throw new Error(result.error || 'Failed to create connection');
      }
      
      analytics.trackEvent(AnalyticsEvents.CONNECTION_CREATED, {
        [AnalyticsParams.SUCCESS]: true,
        [AnalyticsParams.AGE_RATING]: ageRating,
      });
      
      toast({
        title: 'Connection created',
        description: `"${name}" has been successfully created. Scan the QR code to connect.`,
      });
      
      return result.data.id;
    } catch (error) {
      console.error('Error creating connection:', error);
      
      analytics.trackEvent(AnalyticsEvents.CONNECTION_FAILED, {
        [AnalyticsParams.SUCCESS]: false,
        [AnalyticsParams.ERROR_MESSAGE]: error instanceof Error ? error.message : 'Unknown error',
      });
      
      toast({
        title: 'Connection failed',
        description: error instanceof Error ? error.message : 'Failed to create connection. Please try again.',
        variant: 'destructive',
      });
      
      return undefined;
    }
  };

  const handleUpdateConnection = async (id: string, data: { name?: string; ageRating?: string }): Promise<void> => {
    try {
      if (!user) {
        toast({
          title: 'Authentication required',
          description: 'You must be logged in to update a connection.',
          variant: 'destructive',
        });
        return;
      }
      
      const result = await apiUpdateConnection(id, data);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to update connection');
      }
      
      analytics.trackEvent(AnalyticsEvents.FEATURE_USED, {
        [AnalyticsParams.FEATURE_NAME]: 'update_connection',
        [AnalyticsParams.SUCCESS]: true,
      });
      
      toast({
        title: 'Connection updated',
        description: 'The connection has been successfully updated.',
      });
    } catch (error) {
      console.error('Error updating connection:', error);
      
      analytics.trackEvent(AnalyticsEvents.FEATURE_USED, {
        [AnalyticsParams.FEATURE_NAME]: 'update_connection',
        [AnalyticsParams.SUCCESS]: false,
        [AnalyticsParams.ERROR_MESSAGE]: error instanceof Error ? error.message : 'Unknown error',
      });
      
      toast({
        title: 'Update failed',
        description: error instanceof Error ? error.message : 'Failed to update connection. Please try again.',
        variant: 'destructive',
      });
      
      throw error;
    }
  };

  const handleReconnectConnection = async (id: string): Promise<string | undefined> => {
    try {
      if (!user) {
        toast({
          title: 'Authentication required',
          description: 'You must be logged in to reconnect.',
          variant: 'destructive',
        });
        return undefined;
      }
      
      const result = await apiReconnectConnection(id);
      
      if (!result.success || !result.data?.id) {
        throw new Error(result.error || 'Failed to reconnect');
      }
      
      analytics.trackEvent(AnalyticsEvents.CONNECTION_RECONNECT_ATTEMPT, {
        [AnalyticsParams.SUCCESS]: true,
      });
      
      toast({
        title: 'Reconnection initiated',
        description: 'Scan the QR code to reconnect WhatsApp.',
      });
      
      return result.data.id;
    } catch (error) {
      console.error('Error reconnecting:', error);
      
      analytics.trackEvent(AnalyticsEvents.CONNECTION_RECONNECT_ATTEMPT, {
        [AnalyticsParams.SUCCESS]: false,
        [AnalyticsParams.ERROR_MESSAGE]: error instanceof Error ? error.message : 'Unknown error',
      });
      
      throw error;
    }
  };

  const handleDeleteConnection = async (id: string): Promise<void> => {
    try {
      if (!user) {
        toast({
          title: 'Authentication required',
          description: 'You must be logged in to delete a connection.',
          variant: 'destructive',
        });
        return;
      }
      
      const result = await apiDeleteConnection(id);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete connection');
      }
      
      analytics.trackEvent(AnalyticsEvents.FEATURE_USED, {
        [AnalyticsParams.FEATURE_NAME]: 'delete_connection',
        [AnalyticsParams.SUCCESS]: true,
      });
      
      toast({
        title: 'Connection deleted',
        description: 'The connection has been successfully deleted.',
      });
    } catch (error) {
      console.error('Error deleting connection:', error);
      
      analytics.trackEvent(AnalyticsEvents.FEATURE_USED, {
        [AnalyticsParams.FEATURE_NAME]: 'delete_connection',
        [AnalyticsParams.SUCCESS]: false,
        [AnalyticsParams.ERROR_MESSAGE]: error instanceof Error ? error.message : 'Unknown error',
      });
      
      toast({
        title: 'Deletion failed',
        description: error instanceof Error ? error.message : 'Failed to delete connection. Please try again.',
        variant: 'destructive',
      });
      
      throw error;
    }
  };

  return {
    connections,
    loading,
    error,
    createConnection: handleCreateConnection,
    updateConnection: handleUpdateConnection,
    reconnectConnection: handleReconnectConnection,
    deleteConnection: handleDeleteConnection,
  };
};
